"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  extractProperty,
  FacilityContent,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  PortItem,
} from "utils"
import PortContainer from "./PortContainer"
import { Location3Icon, PriceIcon, UserIcon } from "./icons"

const StyledSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 40px 0;
  gap: 24px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 60px 0;
  }
`

const StyledTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 300;
  line-height: 1.5;
  letter-spacing: 0.6px;
  text-transform: capitalize;
  color: ${(props) => props.theme.colors.port.defaultTextColor};

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    letter-spacing: 0;
  }
`

const CARD_WIDTH = 325
const DESKTOP_GAP = 24

const StyledSwiperWrapper = styled.div`
  width: 100%;
  .swiper {
    padding: 0 20px;
    overflow: visible;
  }
  .swiper-wrapper {
    transition-timing-function: ease-in-out;
  }
  .swiper-slide {
    width: ${CARD_WIDTH}px;
    height: auto;
  }
`

const StyledFlexCards = styled.div`
  display: flex;
  gap: ${DESKTOP_GAP}px;
`

const StyledFlexCard = styled.div`
  width: ${CARD_WIDTH}px;
  flex-shrink: 0;
`

const StyledPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledPaginationBullet = styled.button<{ $isActive: boolean }>`
  width: 17px;
  height: 17px;
  padding: 0;
  background-color: transparent;
  border: ${({ $isActive, theme }) =>
    $isActive ? `0.5px solid ${theme.colors.propBlue}` : "none"};
  border-radius: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::after {
    content: "";
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background-color: ${({ $isActive, theme }) =>
      $isActive ? theme.colors.propBlue : theme.colors.port.grey};
    transition: background-color 0.3s ease;
  }

  &:hover {
    border-color: ${(props) => props.theme.colors.propBlue};
  }
`

const StyledCard = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.port.grey};
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`

const StyledCardImage = styled.div`
  width: 100%;
  height: 259px;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 24px;
  gap: 16px;
  flex: 1;
  width: 100%;
`

const StyledCardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 300;
  line-height: 150%;
  text-transform: capitalize;
`

const StyledDetailList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledDetailItem = styled.li`
  list-style: none;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 300;
  line-height: 150%;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledCardButton = styled(Button)`
  margin-top: auto;
  width: 100%;
  border-radius: 45px;
  background-color: ${(props) => props.theme.colors.propBlue};
  color: ${(props) => props.theme.colors.white};
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
  padding: 12px 16px;
  height: 40px;
  text-transform: none;
  &:hover {
    background-color: ${(props) => props.theme.colors.buttonHoverBackgroundColor};
  }
`

type PortAdditionalRoomSuggestionsProps = {
  data: any
  ports?: PortItem[]
  pageData?: any
}

const SuggestionCard = ({ room }: { room: FacilityContent }) => (
  <StyledCard
    href={getNonUmbracoUrl(room.url)}
  >
    <StyledCardImage>
      {room.image && (
        <Image
          src={getUmbracoMediaUrl(room.image)}
          width={325}
          height={259}
          alt={room.facilityName || "Meeting Room"}
        />
      )}
    </StyledCardImage>
    <StyledCardContent>
      <StyledCardTitle>{room.facilityName}</StyledCardTitle>
      <StyledDetailList>
        {room.address && (
          <StyledDetailItem>
            <Location3Icon />
            <span>{room.address}</span>
          </StyledDetailItem>
        )}
        {room.amountOfPeople && (
          <StyledDetailItem>
            <UserIcon />
            <span>{room.amountOfPeople}</span>
          </StyledDetailItem>
        )}
        {room.price && (
          <StyledDetailItem>
            <PriceIcon />
            <span>החל מ- {room.price}</span>
          </StyledDetailItem>
        )}
      </StyledDetailList>
      <StyledCardButton variant="contained" tabIndex={-1} aria-hidden="true">
        להזמנה אונליין
      </StyledCardButton>
    </StyledCardContent>
  </StyledCard>
)

const PortAdditionalRoomSuggestions = ({
  data,
  ports,
  pageData,
}: PortAdditionalRoomSuggestionsProps) => {
  const title = extractProperty(data, "title") || ""
  const swiperRef = useRef<SwiperType | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [needsSwiper, setNeedsSwiper] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)

  const handleBulletClick = useCallback((index: number) => {
    swiperRef.current?.slideTo(index)
  }, [])

  // Get current facility name from page data to exclude it
  const currentFacilityName = pageData?.page ? extractProperty(pageData.page, "facilityName") : ""

  const suggestions: FacilityContent[] = useMemo(() => {
    const allFacilities = ports?.flatMap((port) => port.portFacilities || []) || []
    const meetingRooms = allFacilities.filter(
      (f) =>
        f.facilityType?.toLowerCase().includes("חדר") ||
        f.facilityType?.toLowerCase().includes("meeting"),
    )
    // Exclude the current room
    const filtered = currentFacilityName
      ? meetingRooms.filter((f) => f.facilityName !== currentFacilityName)
      : meetingRooms
    return filtered.slice(0, 3)
  }, [ports, currentFacilityName])

  useEffect(() => {
    const checkWidth = () => {
      const totalCardsWidth =
        suggestions.length * CARD_WIDTH + (suggestions.length - 1) * DESKTOP_GAP
      const availableWidth = wrapperRef.current?.offsetWidth || window.innerWidth
      setNeedsSwiper(totalCardsWidth > availableWidth)
    }
    checkWidth()
    window.addEventListener("resize", checkWidth)
    return () => window.removeEventListener("resize", checkWidth)
  }, [suggestions.length])

  if (suggestions.length === 0) return null

  return (
    <StyledSection ref={wrapperRef}>
      <PortContainer paddingInline={20}>
        {title && <StyledTitle>{title}</StyledTitle>}
      </PortContainer>

      {needsSwiper ? (
        <StyledSwiperWrapper>
          <Swiper
            slidesPerView="auto"
            spaceBetween={16}
            onSwiper={(swiper) => {
              swiperRef.current = swiper
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            breakpoints={{
              768: {
                spaceBetween: 24,
              },
            }}
          >
            {suggestions.map((room) => (
              <SwiperSlide key={room.id}>
                <SuggestionCard room={room} />
              </SwiperSlide>
            ))}
          </Swiper>
          {suggestions.length > 1 && (
            <StyledPagination>
              {suggestions.map((_, index) => (
                <StyledPaginationBullet
                  key={`bullet-${index}`}
                  $isActive={index === activeIndex}
                  onClick={() => handleBulletClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </StyledPagination>
          )}
        </StyledSwiperWrapper>
      ) : (
        <PortContainer paddingInline={20}>
          <StyledFlexCards>
            {suggestions.map((room) => (
              <StyledFlexCard key={room.id}>
                <SuggestionCard room={room} />
              </StyledFlexCard>
            ))}
          </StyledFlexCards>
        </PortContainer>
      )}
    </StyledSection>
  )
}

export default PortAdditionalRoomSuggestions
